import './ConfigColumn.scss';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import cn from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import { isInvalidAnalyticsDimension } from '@blisspointmedia/bpm-types/dist/Columns';
import {
  CROSS_CHANNEL,
  DimensionColumn,
  MetricsTablePreset,
  PACING
} from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { GroupedSegment } from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { DropdownOption } from '@Components/Dropdown/dropdown-constants';
import { Button } from '@Components/Button/Button';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { ColumnMetaDataMap } from '@Components/MetricsTable/metrics-table-utils';
import { useExperimentalAnalyticsCols } from '@src/utils/hooks/useExperimentalAnalyticsCols';
import { useExperimentFlag } from '@src/utils/experiment-context';
import { roleMap } from '@src/Pages/UserAdmin/UserAdmin';
import { SingleChannelMediaTypes } from '@src/utils/performance-utils';
import {
  DeleteColumn,
  DIMENSION_COLUMN_DND_TYPE,
  InvalidGranularityDetails,
  makeInvalidDimMsg,
  MoveColumn
} from '@src/utils/columnMetricSystem/configUtils';
import { defaultIconFor } from '@src/utils/columnMetricSystem/adapters';
import { OnBlurTextInput } from '@Components/OnBlurTextInput';
import { ColumnTop } from './ColumnTop';

const LABEL_FIELD_TEXT = 'Label';

const DATE_GROUPING_OPTIONS: Record<string, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year'
};

interface ConfigDimensionColumnProps {
  addColumn: (i: number) => void;
  color?: string;
  column: DimensionColumn;
  columnMetaDataMap: ColumnMetaDataMap;
  deleteColumn: DeleteColumn;
  dimensionColumns: DimensionColumn[];
  groupedSegments?: Record<string, GroupedSegment[]>;
  hoverItem?: string;
  index: number;
  isOnlyColumn: boolean;
  moveColumn: MoveColumn;
  analyticsIsSource?: boolean;
  prefix: SingleChannelMediaTypes | typeof CROSS_CHANNEL | typeof PACING;
  setPresetChanges: Dispatch<SetStateAction<MetricsTablePreset>>;
  invalidGranularityDetails?: InvalidGranularityDetails;
  incompatibleTableDimsFromFilter?: InvalidGranularityDetails;
}

export const ConfigDimensionColumn = ({
  addColumn,
  color,
  column,
  columnMetaDataMap = {},
  deleteColumn,
  dimensionColumns,
  groupedSegments,
  hoverItem,
  index,
  isOnlyColumn,
  moveColumn,
  analyticsIsSource = false,
  prefix,
  setPresetChanges,
  invalidGranularityDetails = {},
  incompatibleTableDimsFromFilter = {}
}: ConfigDimensionColumnProps) => {
  const { dimensionTypeName, icon, id, label, role, dateGrouping } = column;
  const shouldEnableMetricsTableSegmentGroups = Boolean(
    useExperimentFlag('enableMetricsTableSegmentGroups')
  );
  const expAnalyticsCols = useExperimentalAnalyticsCols();

  const [{ isDragging }, drag, preview] = useDrag<
    { type: typeof DIMENSION_COLUMN_DND_TYPE; id: string; index: number },
    void,
    { isDragging: boolean }
  >({
    type: DIMENSION_COLUMN_DND_TYPE,
    item: { type: DIMENSION_COLUMN_DND_TYPE, id, index },
    collect: monitor => ({ isDragging: monitor.isDragging() })
  });

  const [{ isOver, dropIndex }, drop] = useDrop<
    { type: typeof DIMENSION_COLUMN_DND_TYPE; id: string; index: number },
    void,
    { isOver: boolean; dropIndex: number | undefined }
  >({
    accept: DIMENSION_COLUMN_DND_TYPE,
    canDrop: item => item.id !== id,
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop(),
      dropIndex: monitor.getItem()?.index
    }),
    drop: item => moveColumn(item.id, column.id)
  });

  const hasIconsAvailable = Boolean(columnMetaDataMap[dimensionTypeName]?.iconStyle);
  const defaultIcon = defaultIconFor(columnMetaDataMap[dimensionTypeName]?.iconStyle);
  const hasThumbnailToggle =
    columnMetaDataMap[dimensionTypeName]?.iconStyle === 'thumbnail' &&
    dimensionTypeName === 'Ad Name';
  const thumbnailOn = icon !== null;
  // TVAD (linear/streaming) channels don't offer a Date Grouping picker — Date is always day-level there.

  const columnDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      preview(node);
      drop(node);
    },
    [preview, drop]
  );
  const handleDragRef = useCallback(
    (node: HTMLDivElement | null) => {
      drag(node);
    },
    [drag]
  );

  const onDimensionChange = useCallback(
    <T extends keyof DimensionColumn>(i: number, key: T, value: DimensionColumn[T]) => {
      setPresetChanges(changes => {
        const dimensionColumnsUpdated = changes.dimensionColumns.map((col, colIndex) =>
          colIndex === i
            ? {
                ...col,
                ...(key === 'dimensionTypeName' ? { icon: undefined } : {}),
                [key]: value
              }
            : col
        );
        if (key === 'dimensionTypeName') {
          const metadata = columnMetaDataMap[value as string];
          dimensionColumnsUpdated[i] = {
            ...dimensionColumnsUpdated[i],
            dimensionVarName:
              !metadata && shouldEnableMetricsTableSegmentGroups
                ? (value as DimensionColumn['dimensionVarName'])
                : (metadata?.name as DimensionColumn['dimensionVarName']),
            label: undefined
          };
        }
        return { ...changes, dimensionColumns: dimensionColumnsUpdated };
      });
    },
    [setPresetChanges, columnMetaDataMap, shouldEnableMetricsTableSegmentGroups]
  );

  useEffect(() => {
    // Check to see if the column should have a logo/thumbnail by default. Check explicitly that the
    // value is undefined — null indicates the user directly chose "no icon".
    if (icon === undefined && hasIconsAvailable) {
      onDimensionChange(index, 'icon', defaultIcon);
    }
    if (icon != null && !hasIconsAvailable) {
      onDimensionChange(index, 'icon', null as unknown as DimensionColumn['icon']);
    }
  }, [icon, hasIconsAvailable, defaultIcon, onDimensionChange, index]);

  const typeOptions = useMemo(() => {
    const otherOptions = Object.entries(columnMetaDataMap).reduce<DropdownOption[]>(
      (options, [colPrettyName, colMetaData]) => {
        const filterOut = ['logo', 'icon', 'thumbnail'].some(term =>
          colPrettyName.toLowerCase().includes(term)
        );
        if (filterOut) return options;
        if (
          groupedSegments &&
          shouldEnableMetricsTableSegmentGroups &&
          Object.values(groupedSegments)
            .flatMap(group => Object.values(group))
            .some(segment => segment.segmentName === colPrettyName)
        ) {
          return options;
        }

        const dimVarName = colMetaData?.name ?? colPrettyName;
        const isInvalidAnalyticsDim =
          analyticsIsSource && isInvalidAnalyticsDimension(dimVarName, expAnalyticsCols);
        const isInvalidGranularityDim =
          Boolean(invalidGranularityDetails[dimVarName]) ||
          Boolean(incompatibleTableDimsFromFilter[dimVarName]);
        const disabled = isInvalidAnalyticsDim || isInvalidGranularityDim;
        const tooltipMessage = disabled
          ? makeInvalidDimMsg({
              prettyDims: [colPrettyName],
              isInvalidAnalyticsDim,
              conflictingTableDims: invalidGranularityDetails[dimVarName],
              conflictingFilterDims: incompatibleTableDimsFromFilter[dimVarName]
            })
          : undefined;

        return [
          ...options,
          {
            value: colPrettyName,
            label: colPrettyName,
            disabled,
            simpleTooltip: tooltipMessage,
            simpleTooltipVerticalShift: '-55px'
          }
        ];
      },
      []
    );

    if (groupedSegments && shouldEnableMetricsTableSegmentGroups) {
      Object.keys(groupedSegments).forEach(groupName => {
        otherOptions.push({ label: groupName, value: groupName });
      });
    }
    return otherOptions;
  }, [
    columnMetaDataMap,
    analyticsIsSource,
    groupedSegments,
    shouldEnableMetricsTableSegmentGroups,
    expAnalyticsCols,
    invalidGranularityDetails,
    incompatibleTableDimsFromFilter
  ]);

  return (
    <div className='column-config-outer' ref={columnDropRef}>
      <div
        className={cn('column-config', {
          'is-over': isOver,
          left: isOver && (dropIndex ?? -1) > index,
          right: isOver && (dropIndex ?? -1) <= index,
          dragging: isDragging,
          'is-hover-item': hoverItem === id
        })}
      >
        <ColumnTop
          noHeaders
          color={color}
          handleRef={handleDragRef}
          addColumn={addColumn}
          i={index}
        />
        <div className='basic-section'>
          <Dropdown
            size={Size.MD}
            label={prefix === PACING ? 'Segment' : 'Type'}
            onChange={val => {
              if (
                shouldEnableMetricsTableSegmentGroups &&
                groupedSegments &&
                Object.keys(groupedSegments).includes(val)
              ) {
                onDimensionChange(index, 'dimensionTypeName', val);
                groupedSegments[val].forEach(segment => {
                  addColumn(dimensionColumns.length);
                  onDimensionChange(
                    dimensionColumns.length,
                    'dimensionTypeName',
                    segment.segmentName
                  );
                });
              } else {
                onDimensionChange(index, 'dimensionTypeName', val);
              }
            }}
            options={typeOptions}
            value={dimensionTypeName}
          />
        </div>
        <div className='form-section'>
          <div className='label'>{LABEL_FIELD_TEXT}</div>
          <OnBlurTextInput
            onChange={val => onDimensionChange(index, 'label', val || undefined)}
            placeholder={`${dimensionTypeName} (default)`}
            size='sm'
            value={label ?? ''}
          />
        </div>
        <div className='basic-section'>
          <Dropdown
            size={Size.MD}
            label='Icon'
            disabled={!hasIconsAvailable}
            onChange={val =>
              onDimensionChange(
                index,
                'icon',
                val === 'none'
                  ? (null as unknown as DimensionColumn['icon'])
                  : (val as DimensionColumn['icon'])
              )
            }
            value={icon ?? 'none'}
            options={[
              { label: 'None', value: 'none' },
              { label: 'Has Icon', value: 'hasIcon' },
              { label: 'Icon Only', value: 'iconOnly' }
            ]}
          />
        </div>
        {dimensionTypeName === 'Date' && (
          <div className='basic-section'>
            <Dropdown
              size={Size.MD}
              label='Date Grouping'
              value={dateGrouping ?? 'day'}
              options={Object.keys(DATE_GROUPING_OPTIONS).map(key => ({
                label: DATE_GROUPING_OPTIONS[key],
                value: key
              }))}
              onChange={val =>
                onDimensionChange(index, 'dateGrouping', val as DimensionColumn['dateGrouping'])
              }
            />
          </div>
        )}
        <div className='basic-section'>
          <Dropdown
            size={Size.MD}
            label='Role'
            value={`${role ?? 10}`}
            options={Object.keys(roleMap).map(roleKey => ({
              label: roleMap[Number(roleKey)],
              value: roleKey
            }))}
            onChange={val => onDimensionChange(index, 'role', parseInt(val, 10))}
          />
        </div>
        {hasThumbnailToggle && (
          <div className='basic-section'>
            <ToggleSwitch
              label='Thumbnail'
              checked={thumbnailOn}
              onChange={() =>
                onDimensionChange(
                  index,
                  'icon',
                  thumbnailOn ? (null as unknown as DimensionColumn['icon']) : 'hasIcon'
                )
              }
            />
          </div>
        )}
        {!isOnlyColumn && (
          <Button
            size={Size.MD}
            appearance={Appearance.OUTLINED}
            design={Intent.ERROR}
            className='delete-button'
            onClick={() => deleteColumn(index)}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};
